import { z } from 'zod';

/**
 * The third-party **capabilities** the platform integrates with (N21).
 *
 * A capability is what the platform needs ("store an object", "classify text"),
 * not who provides it — providers are interchangeable behind it, exactly one
 * active at a time. Consumers resolve a capability and never name a vendor.
 *
 * `ai-text`, `ai-embedding` and `face-embedding` are separate so moderation
 * (P20), text similarity and face-match (P13) are configured independently — and
 * because embeddings are Gemini-only at launch, while text has two providers.
 *
 * `face-embedding` is a capability of its own rather than a widening of
 * `ai-embedding` because the two take **incompatible inputs**: an embedding call
 * that accepted either a string or an image would be a discriminated union at
 * every call site, and an operator would be unable to point face-match at one
 * model and semantic search at another.
 *
 * `ai-image` (P27b1) is the vision classifier behind the AI-generated-photo
 * advisory. It is not `face-embedding` (a classification is not an embedding)
 * and not `ai-text` (the input is an image), so an operator points it at a
 * vision model and budgets its per-upload spend independently.
 *
 * `sms` (P30b1) carries one-time codes to phones and nothing else. Appended
 * last: the array backs the `integrations.integration_capability` pg enum.
 */
export const INTEGRATION_CAPABILITIES = [
  'ai-text',
  'ai-embedding',
  'face-embedding',
  'ai-image',
  'storage',
  'push',
  'email',
  'revenuecat',
  'sms',
] as const;

export const IntegrationCapabilitySchema = z.enum(INTEGRATION_CAPABILITIES);

export type IntegrationCapability = (typeof INTEGRATION_CAPABILITIES)[number];

const CAPABILITY_SET: ReadonlySet<string> = new Set<string>(INTEGRATION_CAPABILITIES);

/**
 * Narrows a plain string — what a route parameter passes through before the
 * api can treat it as a capability (an unknown one is a 404, never a lookup
 * against `undefined`).
 */
export function isIntegrationCapability(value: string): value is IntegrationCapability {
  return CAPABILITY_SET.has(value);
}
